import { useEffect, useMemo, useRef } from 'react';
import { useDebounce } from './useDebounce';
import {
  PROJECT_DIFF_FIELDS,
  normalizeProjectFieldValue,
  getChangedProjectFields,
} from '@/utils/projectFormDiff';
import { ProjectCreationValues, ProjectProps } from '@/types';

// TELV1-491 — Draft Safety Net.
//
// This module is a PURE READ-SIDE OBSERVER of the existing form + value-diff
// machinery (`projectFormDiff.ts`). It must never write into the save
// payload path, never call `mutateAsync`, never touch the Zustand
// change-flags, and never run inside `persistProjectPayload` or
// `getChangedProjectFields`'s call sites for the real save — it only reads
// those exports and writes to `localStorage`. See the TELV1-491 binding
// spec, "Overarching verdict".

const DRAFT_VERSION = 1 as const;
const DEBOUNCE_MS = 2000;

export interface ProjectDraftEnvelope {
  version: typeof DRAFT_VERSION;
  savedAt: number;
  /**
   * The server `updatedAt` captured at write time. Load-bearing for the
   * mount-time provenance check (`getDraftRestoreEligibility`) — NEVER
   * compared against a client wall-clock (`savedAt`). Retained separately
   * from `savedAt`, which exists only for human-facing display ("draft from
   * 3 minutes ago").
   */
  baseUpdatedAt: string | null;
  values: Partial<ProjectCreationValues>;
}

const draftKey = (projectId: string): string => `project_draft_${projectId}`;

// Every localStorage op is wrapped so a failed read/write/remove (quota
// errors, Safari private-mode throw-on-write, disabled storage) is
// invisible to the caller and never throws into React render. The feature
// degrades to "no draft safety net"; the real save path is unaffected.

const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Fail open.
  }
};

const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Fail open.
  }
};

/**
 * Removes any persisted draft for a project. A plain, imperatively callable
 * helper (not the hook) so save/publish/delete success handlers can clear
 * the safety net without mounting a hook. Fails open (a throwing storage
 * backend is a silent no-op), matching every other storage op here.
 */
export const clearProjectDraft = (projectId: string | undefined): void => {
  if (!projectId) return;
  safeRemoveItem(draftKey(projectId));
};

/**
 * Snapshots only the fields the save-payload diff engine cares about
 * (`PROJECT_DIFF_FIELDS`), each run through the exact same
 * `normalizeProjectFieldValue` the diff engine itself uses. This both (a)
 * strips `media`/`File` values — via the `interview_questions` case's
 * `stripMedia` — so no non-serializable value ever reaches
 * `JSON.stringify`, and (b) guarantees the draft is stored in the same
 * normalized shape `getChangedProjectFields` compares in, so restore
 * round-trips cleanly.
 */
const buildSnapshot = (values: ProjectCreationValues): Partial<ProjectCreationValues> => {
  const snapshot: Record<string, unknown> = {};
  PROJECT_DIFF_FIELDS.forEach((key) => {
    snapshot[key] = normalizeProjectFieldValue(key, values[key]);
  });
  return snapshot as Partial<ProjectCreationValues>;
};

// TELV1-491 architect review (PR #1656) — array-typed PROJECT_DIFF_FIELDS
// keys. `normalizeProjectFieldValue` turns an empty array into `undefined`
// for these two (see `projectFormDiff.ts`'s `type_of_interview` and
// `screening_questions` cases), which round-trips fine through the diff
// engine itself, but is the wrong shape to hand back to `setValue` on
// restore: `type_of_interview` is bound via multiple `Controller` instances
// (one per checkbox, `Instructions.tsx:105-127`) whose internal RHF
// dirty-tracking throws when set to `undefined` rather than an array
// (`Cannot read properties of undefined (reading 'disabled')`). Restoring
// `[]` instead is both crash-safe and semantically correct — an emptied
// multi-select IS the empty array, not "no value".
const ARRAY_DIFF_FIELD_KEYS = new Set<keyof ProjectCreationValues>([
  'type_of_interview',
  'screening_questions',
]);

/**
 * Resolves the value `handleRestoreDraft` should hand to `setValue` for a
 * single PROJECT_DIFF_FIELDS key. A small, independently testable pure
 * helper (rather than inlining this in `ProjectCreate.tsx`, which isn't
 * feasible to unit-test in isolation) — see `ARRAY_DIFF_FIELD_KEYS` above
 * for why array-typed fields need the `[]` fallback.
 */
export const resolveDraftRestoreValue = (
  key: keyof ProjectCreationValues,
  rawValue: unknown,
): unknown => {
  if (rawValue === undefined && ARRAY_DIFF_FIELD_KEYS.has(key)) return [];
  return rawValue;
};

/**
 * Reads and validates a persisted draft. Any parse failure or a
 * version mismatch is treated as "no draft" and self-heals by removing the
 * stale key — never surfaced to the caller as an error, no banner, no
 * toast.
 */
export const readProjectDraft = (projectId: string | undefined): ProjectDraftEnvelope | null => {
  if (!projectId) return null;
  const raw = safeGetItem(draftKey(projectId));
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    safeRemoveItem(draftKey(projectId));
    return null;
  }

  const envelope = parsed as Partial<ProjectDraftEnvelope> | null;
  if (!envelope || typeof envelope !== 'object' || envelope.version !== DRAFT_VERSION) {
    safeRemoveItem(draftKey(projectId));
    return null;
  }

  return envelope as ProjectDraftEnvelope;
};

export interface DraftRestoreEligibility {
  eligible: boolean;
  /** True when the server's `updatedAt` moved since the draft was captured. */
  updatedElsewhere: boolean;
}

/**
 * Point 4 hardening: never compares a client wall-clock (`savedAt`) to a
 * server wall-clock (`updatedAt`) — clock skew makes that comparison both
 * false-negative (suppress a good draft) and false-positive (restore a
 * stale one). Instead compares `draft.baseUpdatedAt` — the server
 * `updatedAt` captured at write time — against the server's *current*
 * `updatedAt` (provenance, not time).
 *
 * - Equal → the draft was captured against the exact server state now
 *   loaded → eligible, no warning.
 * - Different/newer → the server changed since the draft was captured
 *   (another tab, a collaborator, a completed save elsewhere) → still
 *   eligible, but flagged so the caller can show the "updated elsewhere"
 *   banner copy.
 * - Either timestamp missing → fail open toward preserving the user's work:
 *   eligible, no warning.
 *
 * Also folds in the value-diff predicate (a draft that no longer differs
 * from the server is never restore-eligible, matching the "don't persist a
 * no-op draft" invariant on the write side).
 */
export const getDraftRestoreEligibility = (
  draft: ProjectDraftEnvelope | null,
  serverData: ProjectProps | undefined,
): DraftRestoreEligibility => {
  if (!draft || !serverData) return { eligible: false, updatedElsewhere: false };

  const changed = getChangedProjectFields(draft.values as ProjectCreationValues, serverData);
  if (Object.keys(changed).length === 0) return { eligible: false, updatedElsewhere: false };

  const { baseUpdatedAt } = draft;
  const serverUpdatedAt = serverData.updatedAt ?? null;

  if (baseUpdatedAt == null || serverUpdatedAt == null) {
    return { eligible: true, updatedElsewhere: false };
  }

  return { eligible: true, updatedElsewhere: baseUpdatedAt !== serverUpdatedAt };
};

/**
 * Shared write path used by both the debounced effect and the optional
 * pagehide/visibilitychange flush below — a single place that owns the
 * "never write a no-op draft" invariant and the fail-open `JSON.stringify`
 * guard, so the flush can never diverge from the debounced write's
 * behaviour.
 */
const persistSnapshotIfChanged = (
  projectId: string,
  serializedSnapshot: string,
  serverData: ProjectProps,
): void => {
  let snapshot: Partial<ProjectCreationValues>;
  try {
    snapshot = JSON.parse(serializedSnapshot);
  } catch {
    return;
  }

  // Never write a draft that equals server state — keeps "draft differs
  // from server" true by construction and avoids offering a no-op restore.
  const changed = getChangedProjectFields(snapshot as ProjectCreationValues, serverData);
  if (Object.keys(changed).length === 0) {
    safeRemoveItem(draftKey(projectId));
    return;
  }

  const envelope: ProjectDraftEnvelope = {
    version: DRAFT_VERSION,
    savedAt: Date.now(),
    baseUpdatedAt: serverData.updatedAt ?? null,
    values: snapshot,
  };

  try {
    safeSetItem(draftKey(projectId), JSON.stringify(envelope));
  } catch {
    // JSON.stringify shouldn't throw here (File already stripped by
    // buildSnapshot's normalization), but guard defensively per spec —
    // every JSON.stringify in this module fails open.
  }
};

export interface UseProjectDraftGuards {
  /**
   * TELV1-491 post-ship fix (destructive-restore incident). True only once
   * the `[singleProjectData]` reset effect in `ProjectCreate.tsx` has
   * actually synced `watch()` to the CURRENT project's server values.
   *
   * Root cause this closes: on the very first render where `serverData`
   * loads, RHF's `defaultValues` were captured on an EARLIER render (before
   * any project data existed), so `watch()` still returns stale/near-empty
   * values for at least one more render — the reset effect's `reset()` call
   * doesn't propagate to `watch()` until the render after it runs. Because
   * `useDebounce`'s very first mounted value has no delay, the write-effect
   * below used to fire IMMEDIATELY on that stale snapshot, compare it
   * against the just-loaded server data, see a spurious "diff", and
   * silently WRITE a bogus envelope over any real pre-existing draft — then
   * ~2s later, once the snapshot caught up and matched server, REMOVE the
   * draft entirely. Net effect: any real draft from a previous session was
   * destroyed within ~2-3s of every page load, before the user could act on
   * it. Gating the entire read/write/remove path on `isFormReady` means the
   * hook never evaluates a diff against a form that hasn't been confirmed
   * synced to the current project's server baseline.
   */
  isFormReady: boolean;
  /**
   * TELV1-491 post-ship fix. True while EITHER of two conditions holds:
   * (a) there is a pending, un-actioned draft-restore banner on screen for
   * this project, or (b) a save mutation is currently in flight
   * (`status === 'pending'` on `useMutateProjectUpdate`, per the
   * TELV1-491 architect review, PR #1656). Suppresses BOTH the write and
   * the remove path.
   *
   * (a) is belt-and-suspenders alongside `isFormReady`: even if some future
   * timing issue reintroduces a spurious "form matches server" read while
   * the user hasn't yet chosen Restore/Discard, the real draft must never
   * be touched until they act. `clearProjectDraft` (called explicitly from
   * `handleRestoreDraft` / `handleDiscardDraft`) remains the only way the
   * draft is removed while a banner is showing.
   *
   * (b) closes a narrower, separate transient-window race: a full save's
   * mandatory `clearProjectDraft` call (`ProjectCreate.tsx`,
   * `persistProjectPayload`'s `isFullSave` branch) runs synchronously, but
   * a debounce timer queued from an edit made just before the save can
   * still be pending at that moment. If it fired right after the clear, it
   * would compare the (now-saved, but locally still "dirty" until
   * `reset({}, {keepValues:true})` settles) form against `serverData`,
   * which itself hasn't caught up yet (the `await refetch()` after the
   * clear hasn't resolved) — silently re-writing a draft that looks
   * "unsaved" against stale pre-save server data, undoing the mandatory
   * clear. Freezing writes for the whole `status === 'pending'` window
   * (not just the instant after the clear) is simpler and strictly safer
   * than trying to time a narrower window.
   */
  suppressWrites: boolean;
}

/**
 * Draft safety net: silently snapshots in-progress `ProjectCreationValues`
 * edits to `localStorage` so an accidental reload/tab-close doesn't lose
 * them. See the module doc comment above for the additive-only contract.
 *
 * Callers must gate `projectId` themselves for cases the hook signature
 * doesn't carry a dedicated parameter for — pass `undefined` to suppress
 * writes entirely (e.g. for a read-only viewer: `isUserViewer ? undefined :
 * projectId`). The hook still independently gates on `serverData._id ===
 * projectId` to avoid writing a stale project's residual watched values
 * under the *new* project's key during an SPA project-switch frame
 * (component reused, not remounted).
 *
 * `guards` (added post-ship, TELV1-491 destructive-restore incident — see
 * `UseProjectDraftGuards` doc comments) — both must be satisfied
 * (`isFormReady: true`, `suppressWrites: false`) before this hook will
 * touch `localStorage` at all, on either the debounced path or the flush
 * path.
 */
export function useProjectDraft(
  projectId: string | undefined,
  watchedValues: ProjectCreationValues,
  serverData: ProjectProps | undefined,
  guards: UseProjectDraftGuards,
): void {
  const { isFormReady, suppressWrites } = guards;

  // Debounce the SERIALIZED snapshot (a string), not the raw values object —
  // `watchedValues` (from RHF's `watch()`) is a new object reference on
  // every render even when its content hasn't changed, which would reset a
  // debounce keyed on the object itself every render. Strings compare by
  // value, so an unchanged-content snapshot is a no-op dependency change for
  // `useDebounce`'s effect, matching the existing `watchedJoinValues`
  // pattern (`ProjectCreate.tsx:299`, `:624`).
  //
  // Feed the debounce an empty sentinel while the form isn't confirmed
  // synced to the current project yet, instead of the real (possibly
  // stale/pre-reset) snapshot — see `UseProjectDraftGuards.isFormReady`.
  // The moment `isFormReady` flips true, this becomes a genuine "value
  // changed" transition for `useDebounce` (its hook instance already
  // mounted earlier with the sentinel), so it goes through a full 2s delay
  // rather than firing immediately on a value that might itself still be
  // one render stale.
  //
  // TELV1-491 architect review (PR #1656) — wrapped in `useMemo`.
  // `buildSnapshot` walks the full `interview_questions` tree, so this
  // avoids re-running that walk + `JSON.stringify` on renders that don't
  // touch `isFormReady` or `watchedValues` at all (e.g. a sibling state
  // update elsewhere in `ProjectCreate`). Note this does NOT achieve
  // content-based memoization: `watchedValues` (from RHF's `watch()`) is a
  // new object reference on every render where anything in the form
  // changed, by design — real de-duplication of unchanged-content renders
  // already happens downstream, via `useDebounce`'s string-equality check
  // on the *result* of this computation (see the paragraph above).
  const serializedSnapshot = useMemo(
    () => (isFormReady ? JSON.stringify(buildSnapshot(watchedValues)) : ''),
    [isFormReady, watchedValues],
  );
  const debouncedSnapshot = useDebounce(serializedSnapshot, DEBOUNCE_MS);

  useEffect(() => {
    if (!projectId) return;
    if (!isFormReady || suppressWrites) return;
    if (!debouncedSnapshot) return;
    // Ignore writes until the loaded server data actually belongs to the
    // project currently being edited. See the doc comment above.
    if (serverData?._id !== projectId) return;

    persistSnapshotIfChanged(projectId, debouncedSnapshot, serverData);
  }, [projectId, isFormReady, suppressWrites, debouncedSnapshot, serverData]);

  // Optional pagehide/visibilitychange flush (TELV1-491 spec, residual risk
  // #3): the 2s debounce means a user who edits and closes the tab within
  // that window loses the last delta. This force-writes the LATEST
  // (undebounced) snapshot through the exact same guarded
  // `persistSnapshotIfChanged` path — no new write mechanism, no bypass of
  // the fail-open/no-op-diff guards — whenever the tab is about to be
  // hidden or unloaded. Refs (not state) so the listeners are registered
  // once and always read the latest values without re-subscribing on every
  // keystroke.
  const latestRef = useRef({
    projectId,
    isFormReady,
    suppressWrites,
    serializedSnapshot,
    serverData,
  });
  latestRef.current = { projectId, isFormReady, suppressWrites, serializedSnapshot, serverData };

  useEffect(() => {
    const flush = () => {
      const {
        projectId: currentProjectId,
        isFormReady: currentIsFormReady,
        suppressWrites: currentSuppressWrites,
        serializedSnapshot: currentSnapshot,
        serverData: currentServerData,
      } = latestRef.current;
      if (!currentProjectId) return;
      if (!currentIsFormReady || currentSuppressWrites) return;
      if (!currentSnapshot) return;
      if (currentServerData?._id !== currentProjectId) return;
      persistSnapshotIfChanged(currentProjectId, currentSnapshot, currentServerData);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };

    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // Registered once — `flush` always reads the latest values via
    // `latestRef`, so it never needs to be re-subscribed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
